package vegasful.admin.views

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import vegasful.admin.Application
import vegasful.admin.api.ThingStatus
import vegasful.admin.api.client.AdminThingToDo
import vegasful.admin.api.client._AdminThingToDoQuery
import vegasful.admin.components.*

/**
 * The whole catalogue in one query.
 *
 * `AdminThingsToDo.all` is deliberately unpaged — there are a few hundred things to do city-wide,
 * which is small enough that paging would be ceremony. Only the local layer is read: `resolved`
 * would re-resolve every record (venue name, inherited tags) to render a list that does not need it.
 */
private fun _AdminThingToDoQuery.thingListQuery() {
    this.id
    this.local {
        this.name
        this.status
        this.hours
        this.rankingScore
        this.venue {
            this.id
            this.name
        }
    }
}

@Composable
fun thingsToDo() {
    var things by remember { mutableStateOf<List<AdminThingToDo>?>(null) }
    var filter by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        things = Application.mutate {
            admin {
                thingsToDo {
                    all {
                        thingListQuery()
                    }
                }
            }
        }.admin.thingsToDo.all
    }

    contentContainer {
        entityView {
            title = "Things to Do"

            action {
                this.title = "Add"
                this.primary = true
                this.link = "content/things/add"
            }

            content {
                box({
                    title = "Catalogue"
                    paddedContent = false
                }) {
                    val loaded = things

                    if (loaded == null) {
                        boxSpinner("Loading things to do…")
                    } else {
                        Div({ style { padding(10.px, 16.px) } }) {
                            dialogField("Filter") {
                                simpleTextField(filter.ifBlank { null }) { filter = it.orEmpty() }
                            }
                            Div({ style { fontSize(0.8.cssRem); opacity(.7) } }) {
                                val withoutHours = loaded.count { it.local.hours.isNullOrBlank() }
                                Text(
                                    "${loaded.size} record${if (loaded.size == 1) "" else "s"}" +
                                            if (withoutHours > 0) " · $withoutHours with no hours recorded" else ""
                                )
                            }
                        }

                        val visible = loaded
                            .filter { thing ->
                                filter.isBlank() ||
                                        thing.id.contains(filter, ignoreCase = true) ||
                                        thing.local.name?.contains(filter, ignoreCase = true) == true ||
                                        thing.local.venue?.name?.contains(filter, ignoreCase = true) == true
                            }
                            .sortedBy { it.local.name?.lowercase() ?: it.id }

                        if (visible.isEmpty()) {
                            boxMessage(
                                if (loaded.isEmpty()) "There are no things to do yet."
                                else "No things to do match that filter."
                            )
                        } else {
                            table<AdminThingToDo> {
                                items(visible)

                                column("Name") {
                                    content { thing ->
                                        A(href = "#content/things/${thing.id}") {
                                            Text(thing.local.name ?: thing.id)
                                        }
                                    }
                                }

                                column("Venue") {
                                    content { thing ->
                                        thing.local.venue?.let { venue ->
                                            A(href = "#content/venues/${venue.id}") {
                                                Text(venue.name)
                                            }
                                        } ?: Text("—")
                                    }
                                }

                                column("Hours") {
                                    width = 110.px
                                    content { thing ->
                                        // Which records still need hours entered is the single most
                                        // useful thing this list can say while the catalogue is
                                        // being built out.
                                        Text(if (thing.local.hours.isNullOrBlank()) "not set" else "set")
                                    }
                                }

                                column("Score") {
                                    width = 80.px
                                    content { thing ->
                                        Text(thing.local.rankingScore?.toString() ?: "—")
                                    }
                                }

                                column("Status") {
                                    width = 150.px
                                    content { thing ->
                                        Text(
                                            (thing.local.status ?: ThingStatus.OPEN).name
                                                .lowercase().replace("_", " ")
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
